import { CDN_ENDPOINT, NO_IMAGE } from "../constants";

/**
 * Convert text to URL-friendly slug format
 * @param {string} text - Text to slugify
 * @param {string} local - Locale (e.g., 'ar' for Arabic)
 * @returns {string} Slugified text
 */
export function slugify(text, local) {
  if (local === "ar" && text) return text.replace(" ", "-");
  try {
    return text
      .toString()
      .toLowerCase()
      .replace(new RegExp("[àáâãäå]", "g"), "a") // handling diactritics
      .replace(new RegExp("æ", "g"), "ae")
      .replace(new RegExp("ç", "g"), "c")
      .replace(new RegExp("[èéêë]", "g"), "e")
      .replace(new RegExp("[ìíîï]", "g"), "i")
      .replace(new RegExp("ñ", "g"), "n")
      .replace(new RegExp("[òóôõö]", "g"), "o")
      .replace(new RegExp("œ", "g"), "oe")
      .replace(new RegExp("[ùúûü]", "g"), "u")
      .replace(new RegExp("[ýÿ]", "g"), "y")
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars except hyphens
      .replace(/-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  } catch (e) {
    return "default";
  }
}

/**
 * Transform image URL to use CDN with optimizations
 * @param {string} url - Original image URL
 * @param {string} width - Width parameter (default: "w_800")
 * @param {string} extension - Extension parameter (default: "f_jpg")
 * @returns {string} CDN-optimized image URL
 */
export function cdnImage(url, width = "w_800", extension = "f_jpg") {
  if (!url) return NO_IMAGE;
  const urlOne = url.replace(
    "https://productionvisittanger.s3.eu-central-1.amazonaws.com/uploads",
    CDN_ENDPOINT
  );

  return urlOne.replace(
    CDN_ENDPOINT,
    "https://cdn.visitbenimellal.com/fetch/c_scale,fl_progressive,q_auto," +
      width +
      "/" +
      extension +
      "/" +
      CDN_ENDPOINT
  );
}

/**
 * Format distance in meters for display
 * @param {number} m - Distance in meters
 * @returns {string} Formatted distance string
 */
export function meterDisplay(m) {
  const realLifeDistanceApprox = m * 1.3;
  if (realLifeDistanceApprox < 1000) {
    return realLifeDistanceApprox.toFixed(0) + " m";
  }

  if (realLifeDistanceApprox >= 1000) {
    return (realLifeDistanceApprox / 1000).toFixed(2) + " km";
  }
}

/**
 * Format duration in minutes using locale-aware formatting
 * @param {number} minutes - Duration in minutes
 * @param {string} locale - Locale code (e.g., 'en', 'fr', 'ar')
 * @returns {string} Formatted duration string
 */
export function formatDuration(minutes, locale = 'en') {
  if (!minutes || isNaN(minutes)) {
    return null;
  }

  const duration = Number(minutes);
  
  // Use Intl.RelativeTimeFormat for consistent formatting
  try {
    // For durations, we'll use a simple approach with units
    if (duration < 60) {
      // Use Intl.NumberFormat for consistent number formatting
      const formatter = new Intl.NumberFormat(locale);
      const formattedNumber = formatter.format(duration);
      
      // Handle pluralization based on locale
      if (locale === 'ar') {
        return duration === 1 ? `${formattedNumber} دقيقة` : `${formattedNumber} دقيقة`;
      } else if (locale === 'fr') {
        return duration === 1 ? `${formattedNumber} minute` : `${formattedNumber} minutes`;
      } else if (locale === 'es') {
        return duration === 1 ? `${formattedNumber} minuto` : `${formattedNumber} minutos`;
      } else if (locale === 'de') {
        return duration === 1 ? `${formattedNumber} Minute` : `${formattedNumber} Minuten`;
      } else {
        // Default to English
        return duration === 1 ? `${formattedNumber} min` : `${formattedNumber} mins`;
      }
    } else {
      // For hours, convert and format
      const hours = Math.floor(duration / 60);
      const mins = duration % 60;
      
      const hourFormatter = new Intl.NumberFormat(locale);
      const minFormatter = new Intl.NumberFormat(locale);
      
      if (mins === 0) {
        if (locale === 'ar') {
          return hours === 1 ? `${hourFormatter.format(hours)} ساعة` : `${hourFormatter.format(hours)} ساعة`;
        } else if (locale === 'fr') {
          return hours === 1 ? `${hourFormatter.format(hours)} heure` : `${hourFormatter.format(hours)} heures`;
        } else if (locale === 'es') {
          return hours === 1 ? `${hourFormatter.format(hours)} hora` : `${hourFormatter.format(hours)} horas`;
        } else if (locale === 'de') {
          return hours === 1 ? `${hourFormatter.format(hours)} Stunde` : `${hourFormatter.format(hours)} Stunden`;
        } else {
          return hours === 1 ? `${hourFormatter.format(hours)} hour` : `${hourFormatter.format(hours)} hours`;
        }
      } else {
        // Format both hours and minutes
        const formattedHours = hourFormatter.format(hours);
        const formattedMins = minFormatter.format(mins);
        
        if (locale === 'ar') {
          return `${formattedHours} ساعة ${formattedMins} دقيقة`;
        } else if (locale === 'fr') {
          return `${formattedHours}h ${formattedMins}min`;
        } else if (locale === 'es') {
          return `${formattedHours}h ${formattedMins}min`;
        } else if (locale === 'de') {
          return `${formattedHours}h ${formattedMins}min`;
        } else {
          return `${formattedHours}h ${formattedMins}min`;
        }
      }
    }
  } catch (error) {
    // Fallback to simple format if Intl is not supported
    return `${duration} mins`;
  }
}

/**
 * Format currency using locale-aware formatting
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'MAD' for Moroccan Dirham)
 * @param {string} locale - Locale code (e.g., 'en', 'fr', 'ar')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'MAD', locale = 'en') {
  if (!amount || isNaN(amount)) {
    return null;
  }

  const numericAmount = Number(amount);
  
  try {
    // Use Intl.NumberFormat for currency formatting
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    
    return formatter.format(numericAmount);
  } catch (error) {
    // Fallback for unsupported currencies or locales
    try {
      // Try with basic number formatting and currency symbol
      const numberFormatter = new Intl.NumberFormat(locale);
      const formattedNumber = numberFormatter.format(numericAmount);
      
      // Add currency symbol based on currency code
      if (currency === 'MAD') {
        if (locale === 'ar') {
          return `${formattedNumber} درهم`;
        } else {
          return `${formattedNumber} DH`;
        }
      } else if (currency === 'EUR') {
        return `${formattedNumber} €`;
      } else if (currency === 'USD') {
        return `$${formattedNumber}`;
      } else {
        return `${formattedNumber} ${currency}`;
      }
    } catch (fallbackError) {
      // Last resort fallback
      return `${numericAmount} ${currency === 'MAD' ? 'DH' : currency}`;
    }
  }
}